import type { ResponsiveNotePhoto } from "@rebuild/shared/models";

/**
 * Gallery thumbnails are generated off-request, so re-reading the photo is the only way a
 * client learns one is ready. Nothing in the response distinguishes "still working" from
 * "no job will ever finish this", so polling has to stop on its own: poll while a thumbnail
 * is plausibly seconds away, back off while a large batch drains, then give up. Giving up is
 * safe — the gallery already falls back to the original image, and the next ordinary refetch
 * picks the thumbnail up. This matters more here than on mobile: each poll re-reads every
 * note and photo in the estimate and re-signs a url per photo.
 */
const FAST_POLL_UNTIL_MS = 30_000;
const POLL_DEADLINE_MS = 5 * 60_000;

interface ThumbnailPollIntervals {
  fastMs: number;
  slowMs: number;
}

export function responsiveNotePhotoThumbnailPollInterval(
  photos: Pick<ResponsiveNotePhoto, "thumbnailStatus" | "createdAt">[] | undefined,
  { fastMs, slowMs }: ThumbnailPollIntervals,
): number | false {
  if (!photos?.length) return false;
  const now = Date.now();
  // Pace against the newest waiting photo: a fresh upload should restore fast polling even
  // when an older photo has been stuck past the deadline. Client and server clocks can
  // disagree in either direction, so only the size of the gap is meaningful.
  const newestWaitingAgeMs = photos.reduce<number | undefined>((newestAgeMs, photo) => {
    if (photo.thumbnailStatus !== "pending" && photo.thumbnailStatus !== "processing") return newestAgeMs;
    const ageMs = Math.abs(now - photo.createdAt);
    return newestAgeMs === undefined || ageMs < newestAgeMs ? ageMs : newestAgeMs;
  }, undefined);

  if (newestWaitingAgeMs === undefined || newestWaitingAgeMs >= POLL_DEADLINE_MS) return false;
  return newestWaitingAgeMs < FAST_POLL_UNTIL_MS ? fastMs : slowMs;
}
